import {Inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {from, Observable, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {ServerConstants} from '../model/ServerConstants';
import {CommonRzmService} from './common-rzm.service';
import {RestEndPoints} from '../model/RestEndPoints';
import {AuthService} from './auth.service';
import {ChangeRequests, Delegation, TopLevelDomain} from '@iana-rzms/bff-sdk';
import {BffClientService} from './bff-client.service';
import {UpdateChangeRequestCommand} from '../generate/UpdateChangeRequestCommand';
import {RevokeTopLevelDomainCommand} from '../generate/RevokeTopLevelDomainCommand';
import {ChangeRequestVO, TldChangeResultVO} from '../generate/TldChangeResultVO';
import {TransferTopLevelDomainCommand} from '../generate/TransferTopLevelDomainCommand';
import {RequestMetadata} from '../generate/RequestMetadata';
import {ChangeVO} from '../generate/ChangeVO';

@Injectable()
export class DomainService extends CommonRzmService {

  constructor(
    @Inject(HttpClient) http,
    @Inject('env') environment,
    @Inject(AuthService) auth,
    bffClient: BffClientService
  ) {
    super(http, environment, auth, bffClient);
  }

  /**
   * @deprecated This doesn't appear to be called anywhere
   */
  getDomain(name: string): Observable<TopLevelDomain> {
    const url = this.getRestEndPoint() + RestEndPoints.GET_DOMAIN + RestEndPoints.URL_SEPARATER + name;
    return this.getResponse<TopLevelDomain>(url);
  }

  getDomainByID(id: string): Observable<TopLevelDomain> {
    const url = this.getRestEndPoint() + RestEndPoints.GET_DOMAIN_BY_ID + RestEndPoints.URL_SEPARATER + id;
    return this.getResponse<TopLevelDomain>(url);
  }

  getDomainOpenChangeRequests(id: string): Observable<any> {
    const url = this.getRestEndPoint() + RestEndPoints.GET_DOMAIN_OPEN_CHANGE_REQUESTS_BY_ID + RestEndPoints.URL_SEPARATER + id;
    return this.getResponse<any>(url);
  }

  isDomainModified(domain: TopLevelDomain): Observable<any> {
    const url = this.getRestEndPoint() + RestEndPoints.GET_DOMAIN_DIFF;
    return this.sendPostRequest<ChangeVO>(url, domain);
  }

  splitRequest(domain: TopLevelDomain): Observable<ChangeRequestVO[]> {
    const url = this.getRestEndPoint() + RestEndPoints.GET_DIFF_AND_SPLIT;
    return this.sendPostRequest<ChangeRequestVO[]>(url, domain);
  }

  confirmChangeRequest(domain: TopLevelDomain, requestMetadata: RequestMetadata): Observable<TldChangeResultVO> {
    if (!requestMetadata || !requestMetadata.requestType) {
      throwError('Request Type is empty');
    }

    if (requestMetadata.requestType === 'REVOKE') {
      return this.confirmRevokeRequest(domain, requestMetadata);
    } else if (requestMetadata.requestType === 'NEW') {
      return this.confirmNewDelegationRequest(domain, requestMetadata);
    } else if (requestMetadata.requestType === 'TRANSFER') {
      return this.transferDelegationRequest(domain, requestMetadata);
    } else if (requestMetadata.requestType === 'UPDATE') {
      return this.updateDomainRequest(domain, requestMetadata);
    } else if (requestMetadata.requestType === 'DIRECT') {
      return this.directEditDomainRequest(domain, requestMetadata);
    }
  }

  updateExistingChangeRequest(domain: TopLevelDomain, requestMetadata: RequestMetadata) {
    const url = this.getRestEndPoint() + RestEndPoints.COMMON_COMMAND;

    const updateChangeRequestCommand: UpdateChangeRequestCommand = {};
    updateChangeRequestCommand.topLevelDomainVO = domain as any;
    updateChangeRequestCommand.changeRequest = requestMetadata.ticketNumber;
    updateChangeRequestCommand.comment = requestMetadata.comment;
    return this.sendPostRequest<TldChangeResultVO>(url, this.createCommandEnvelope(updateChangeRequestCommand, ServerConstants.UPDATE_CHANGE_REQUEST_COMMAND));
  }

  directEditDomainRequest(domain: TopLevelDomain, requestMetadata: RequestMetadata): Observable<TldChangeResultVO> {
    const metadata: RequestMetadata = {
      submissionOriginType: 'Web',
      ...requestMetadata,
      requestType: 'DIRECT'
    };

    return from(ChangeRequests.updateTopLevelDomain(this.sdkOptions({
      responseStyle: 'data',
      body: {
        topLevelDomain: domain,
        requestMetadata: metadata
      },
      // Skip Zod client-side validation: root domain (.) has contacts with null names
      // which the generated Zod schema rejects. The backend handles its own validation.
      requestValidator: false
    } as any)).catch(error => {
      throw error;
    })).pipe(
      map(res => {
        if (res && (res as any).error) {
          throw (res as any).error;
        }
        return {
          changeRequestVO: (res as any).changeRequests || []
        };
      }),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  /**
   * @deprecated TODO: Replace with SDK in future ticket (out of RZ-2684 scope)
   * TRANSFER operation for staff - moving domain between registries.
   * Currently uses legacy TransferTopLevelDomainCommand.
   */
  transferDelegationRequest(domain: TopLevelDomain, requestMetadata: RequestMetadata): Observable<TldChangeResultVO> {
    const url = this.getRestEndPoint() + RestEndPoints.COMMON_COMMAND;

    const transferTopLevelDomainCommand: TransferTopLevelDomainCommand = {};
    transferTopLevelDomainCommand.TopLevelDomainVO = domain as any;
    transferTopLevelDomainCommand.requestMetadata = requestMetadata;
    transferTopLevelDomainCommand.skipRegistryUrlValidation = !!(domain as any).ignoreRegistrySSLError;
    transferTopLevelDomainCommand.skipRdapValidation = !!(domain as any).ignoreRDAPError;
    return this.sendPostRequest<TldChangeResultVO>(url, this.createCommandEnvelope(transferTopLevelDomainCommand, ServerConstants.TRANSFER_TLD_COMMAND));
  }

  updateDomainRequest(domain: TopLevelDomain, requestMetadata: RequestMetadata): Observable<TldChangeResultVO> {
    const metadata: RequestMetadata = {
      submissionOriginType: 'Web',
      ...requestMetadata,
      requestType: 'UPDATE'
    };

    return from(ChangeRequests.updateTopLevelDomain(this.sdkOptions({
      responseStyle: 'data',
      body: {
        topLevelDomain: domain,
        requestMetadata: metadata
      },
      // Skip Zod client-side validation: root domain (.) has contacts with null names
      // which the generated Zod schema rejects. The backend handles its own validation.
      requestValidator: false
    } as any)).catch(error => {
      throw error;
    })).pipe(
      map(res => {
        if (res && (res as any).error) {
          throw (res as any).error;
        }
        return {
          changeRequestVO: (res as any).changeRequests || []
        };
      }),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  /**
   * @deprecated TODO: replace in RZ-2683 - Did we miss this in 2683?
   */
  confirmRevokeRequest(domain: TopLevelDomain, requestMetadata: RequestMetadata): Observable<TldChangeResultVO> {
    const url = this.getRestEndPoint() + RestEndPoints.COMMON_COMMAND;

    const revokeTopLevelDomainCommand: RevokeTopLevelDomainCommand = {};
    revokeTopLevelDomainCommand.tld = domain.id;
    revokeTopLevelDomainCommand.requestMetadata = requestMetadata;
    return this.sendPostRequest<TldChangeResultVO>(url, this.createCommandEnvelope(revokeTopLevelDomainCommand, ServerConstants.REVOKE_TLD_COMMAND));
  }

  confirmNewDelegationRequest(domain: TopLevelDomain, requestMetadata: RequestMetadata): Observable<TldChangeResultVO> {
    return from(Delegation.updateDelegation(this.sdkOptions({
      path: {
        topLevelDomainId: domain.id
      },
      body: {
        topLevelDomain: domain,
        requestMetadata: requestMetadata
      }
    }))).pipe(
      map(response => {
        if (response.error) {
          throw response.error;
        }
        return {
          changeRequestVO: [response.data]
        } as TldChangeResultVO;
      }),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  getListOfDomains(page: number, size: number, filter: string, searchText: string): Observable<any> {
    let url = this.getRestEndPoint() + RestEndPoints.GET_LIST_OF_DOMAINS + '?page=' + page + '&size=' + size;
    if (filter && filter != 'All') {
      url += '&filter=' + filter;
    }
    if (searchText) {
      url += '&search=' + searchText;
    }
    return this.getResponse<any>(url);
  }

  createContactsIfNull(domain: TopLevelDomain) {
    // Root domain has no administrative, technical, or organization contacts
    if (domain.type === 'ROOT') {
      return;
    }

    if (domain.organization == null) {
      domain.organization = {
        organizationName: '',
        address: {}
      };
    }

    if (domain.administrative == null) {
      domain.administrative = {
        name: '',
        address: {}
      };
    }

    if (domain.technical == null) {
      domain.technical = {
        name: '',
        address: {}
      };
    }
  }

}
